import { useMemo, useState } from 'react';
import { Link, useParams, useSearch } from '@tanstack/react-router';
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  GitBranchPlus,
  Pencil,
  Printer,
  Send,
  Sparkles,
} from 'lucide-react';
import { Button, cn, MathText } from '@jeraisonne/ui';
import {
  CARD_STATUS_LABELS,
  buildCardQrUrl,
  cardContentSchema,
  cardPalette,
  effectiveCardQuestionXp,
  cardPublishIssues,
  cardPublishWarnings,
  estimateCardFit,
  type CardContent,
  type CardCreature,
  type CardFit,
} from '@jeraisonne/db/cards';
import {
  useCard,
  useGenerateCard,
  usePublishCard,
  useSaveCardContent,
  useSetCardStatus,
  useUpdateCard,
  type CardInput,
  type CardRow,
} from '@/lib/cards/api';
import { CardRecto, CardVerso, ScaledCard } from '@/features/cards/card-faces';
import { CardSectionsEditor } from '@/features/cards/card-sections-editor';
import { CardInfoForm } from '@/features/cards/card-info-form';
import { CardFiliationEditor } from '@/features/cards/card-filiation-editor';
import { CardQuestionsPanel } from '@/features/cards/card-questions-panel';
import { CardToolsEditor } from '@/features/cards/card-tools-editor';
import { CardCreaturePanel } from '@/features/cards/card-creature-panel';
import { CardAnimationPanel } from '@/features/cards/card-animation-panel';
import { isAdmin, useAuth } from '@/lib/auth';
import { AiModelSelect, CARD_DEFAULT_MODEL } from '@/features/cards/ai-model-select';
import type { PipelineModelId } from '@/lib/admin/pipeline-models';
import { CARD_STATUS_STYLES } from '@/routes/_app.tutor.cards';

const FIT_LABEL: Record<CardFit, { text: string; className: string }> = {
  ok: { text: 'Tient bien sur la carte', className: 'bg-xp-100 text-xp-700' },
  tight: { text: 'Carte dense — vérifie l’aperçu', className: 'bg-accent-50 text-accent-600' },
  overflow: { text: 'Déborde de la carte (63 × 88)', className: 'bg-error-50 text-error-600' },
};

function toInput(card: CardRow): CardInput {
  return {
    title: card.title,
    card_type: card.card_type,
    subject: card.subject,
    topic: card.topic,
    domain_label: card.domain_label,
    when_to_use: card.when_to_use,
    rule: card.rule,
    method: card.method,
    example: card.example,
    pitfalls: card.pitfalls,
    source_text: card.source_text ?? '',
    xp_max: card.xp_max,
    difficulty: card.difficulty,
    importance: card.importance,
    rarity: card.rarity,
    color_theme: card.color_theme,
    parent_card_id: card.parent_card_id,
    question_xp: card.question_xp ?? null,
    levels: card.levels,
    lesson_ids: card.lessons.map((l) => l.id),
  };
}

/** Une carte publiée puis modifiée n'est pas encore en ligne dans sa version actuelle. */
export function hasUnpublishedChanges(
  card: Pick<CardRow, 'status' | 'published_at' | 'updated_at'>,
) {
  if (card.status !== 'published' || !card.published_at) return false;
  return new Date(card.updated_at).getTime() > new Date(card.published_at).getTime() + 1000;
}

/**
 * Route tuteur `/tutor/cards/$cardId` — aperçu recto/verso à l'échelle, édition
 * du recto par sections, régénération IA, relecture, publication, impression.
 */
export function TutorCardDetailPage() {
  const { cardId } = useParams({ from: '/app/tutor/cards/$cardId' });
  const search = useSearch({ from: '/app/tutor/cards/$cardId' });
  const card = useCard(cardId);
  // Le message survit au remontage de l'éditeur (clé = updated_at) après une action.
  const [message, setMessage] = useState<CardMessage | null>(
    search.generationError ? { kind: 'error', text: search.generationError } : null,
  );

  if (card.isLoading) {
    return <p className="text-ink-500 px-4 py-6 text-sm">Chargement…</p>;
  }
  if (card.error || !card.data) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-6">
        <p role="alert" className="text-accent-600 text-sm">
          Carte introuvable.
        </p>
        <Link to="/tutor/cards" className="text-primary-600 text-sm underline">
          Retour aux cartes
        </Link>
      </div>
    );
  }
  // Remonte l'éditeur quand le recto change côté serveur (régénération).
  return (
    <CardDetail
      key={`${card.data.id}:${card.data.updated_at}`}
      card={card.data}
      message={message}
      setMessage={setMessage}
    />
  );
}

interface CardMessage {
  kind: 'ok' | 'error';
  text: string;
}

function CardDetail({
  card,
  message,
  setMessage,
}: {
  card: CardRow;
  message: CardMessage | null;
  setMessage: (m: CardMessage | null) => void;
}) {
  const generate = useGenerateCard();
  const saveContent = useSaveCardContent();
  const setStatus = useSetCardStatus();
  const publish = usePublishCard();
  const update = useUpdateCard();
  const { profile, user } = useAuth();
  const admin = isAdmin(profile, user?.email);

  const [draft, setDraft] = useState<CardContent>(card.structured_content);
  const [draftValid, setDraftValid] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [model, setModel] = useState<PipelineModelId>(CARD_DEFAULT_MODEL);
  const [editingInfo, setEditingInfo] = useState(false);
  const [previewEvolved, setPreviewEvolved] = useState(false);
  const [importedCreature, setImportedCreature] = useState<CardCreature | null>(null);
  const palette = cardPalette(card.levels, card.color_theme);

  const previewContent = useMemo(() => {
    const parsed = cardContentSchema.safeParse(draft);
    return parsed.success ? parsed.data : card.structured_content;
  }, [draft, card.structured_content]);
  const fit = estimateCardFit(card.title, previewContent);
  const issues = cardPublishIssues({
    title: card.title,
    cardType: card.card_type,
    subject: card.subject,
    levels: card.levels,
    content: card.structured_content,
    xpMax: card.xp_max,
    status: card.status,
    reviewed: card.status === 'review' || card.status === 'published',
  });
  const warnings = cardPublishWarnings({ title: card.title, content: card.structured_content });
  const archived = card.status === 'archived';
  const pending =
    generate.isPending || saveContent.isPending || setStatus.isPending || publish.isPending;

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setMessage(null);
    try {
      await fn();
      setMessage({ kind: 'ok', text: ok });
    } catch (e) {
      setMessage({ kind: 'error', text: e instanceof Error ? e.message : 'Action impossible.' });
    }
  };

  const onGenerate = () => {
    if (
      dirty &&
      !window.confirm('Régénérer remplacera tes modifications non enregistrées. Continuer ?')
    ) {
      return;
    }
    void run(
      () =>
        generate.mutateAsync({
          card_id: card.id,
          instructions: remarks.trim() || undefined,
          model,
        }),
      'Recto généré. Relis-le avant de le valider.',
    );
  };

  const onSave = () =>
    run(async () => {
      await saveContent.mutateAsync({ id: card.id, content: draft, status: card.status });
      setDirty(false);
    }, 'Recto enregistré.');

  const onValidate = () =>
    run(
      () => setStatus.mutateAsync({ id: card.id, status: 'review' }),
      'Contenu validé : tu peux publier.',
    );

  const onPublish = () =>
    run(() => publish.mutateAsync(card.id), 'Carte publiée : le QR code mène à cette version.');

  const onArchive = () => {
    if (!window.confirm('Archiver la carte ? Elle reste consultable via son QR code.')) return;
    void run(() => setStatus.mutateAsync({ id: card.id, status: 'archived' }), 'Carte archivée.');
  };

  const onRestore = () =>
    run(
      () =>
        setStatus.mutateAsync({
          id: card.id,
          status: card.published_version ? 'published' : 'generated',
        }),
      'Carte restaurée.',
    );

  const onSaveInfo = (input: CardInput) =>
    run(async () => {
      await update.mutateAsync({ id: card.id, input });
      setEditingInfo(false);
    }, 'Informations enregistrées.');

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 pb-[max(env(safe-area-inset-bottom),1.5rem)] sm:py-8">
      <Link
        to="/tutor/cards"
        className="text-ink-500 hover:text-ink-800 mb-2 inline-flex min-h-[2.75rem] items-center gap-1.5 text-sm"
      >
        <ArrowLeft className="h-4 w-4" /> SKILLCARDS
      </Link>
      <header className="mb-5 space-y-2">
        <h1 className="font-display text-ink-900 text-xl font-extrabold leading-tight sm:text-2xl">
          <MathText>{card.title}</MathText>
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-bold uppercase',
              CARD_STATUS_STYLES[card.status],
            )}
          >
            {CARD_STATUS_LABELS[card.status]}
          </span>
          {card.published_version && (
            <span className="text-ink-500 text-xs">
              Version en ligne : v{card.published_version}
            </span>
          )}
          {hasUnpublishedChanges(card) && (
            <span className="bg-accent-50 text-accent-600 rounded-full px-2 py-0.5 text-xs font-semibold">
              Modifications non publiées
            </span>
          )}
          <span className="text-ink-500 text-xs">{card.xp_max} HORA max</span>
        </div>
        <Link
          to="/card/$cardId"
          params={{ cardId: card.id }}
          className="text-primary-600 inline-flex min-h-[2.75rem] items-center gap-1.5 text-sm font-semibold"
        >
          <ExternalLink className="h-4 w-4" aria-hidden />
          Page de la SKILLCARD
          {!card.published_version && (
            <span className="text-ink-400 font-normal">(visible une fois publiée)</span>
          )}
        </Link>
      </header>

      {message && (
        <p
          role={message.kind === 'error' ? 'alert' : 'status'}
          className={cn(
            'mb-4 rounded-xl px-3 py-2 text-sm',
            message.kind === 'error' ? 'bg-error-50 text-error-600' : 'bg-xp-50 text-xp-700',
          )}
        >
          {message.text}
        </p>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
        {/* Aperçu */}
        <section aria-label="Aperçu de la carte" className="space-y-4">
          <div
            role="group"
            aria-label="Forme de la créature"
            className="bg-ink-100 grid grid-cols-2 gap-1 rounded-xl p-1"
          >
            {(
              [
                [false, 'Forme de base'],
                [true, 'Forme évoluée'],
              ] as const
            ).map(([evo, label]) => (
              <button
                key={label}
                type="button"
                aria-pressed={previewEvolved === evo}
                onClick={() => setPreviewEvolved(evo)}
                className={cn(
                  'min-h-[2.75rem] rounded-lg text-sm font-semibold transition-colors active:scale-[0.98]',
                  previewEvolved === evo ? 'text-ink-900 bg-white shadow-sm' : 'text-ink-500',
                )}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1">
            <div>
              <p className="text-ink-500 mb-1 text-xs font-semibold uppercase">Recto</p>
              <ScaledCard>
                <CardRecto
                  title={card.title}
                  cardType={card.card_type}
                  content={previewContent}
                  levels={card.levels}
                  colorTheme={card.color_theme}
                  evolved={previewEvolved}
                />
              </ScaledCard>
            </div>
            <div>
              <p className="text-ink-500 mb-1 text-xs font-semibold uppercase">Verso</p>
              <ScaledCard>
                <CardVerso
                  cardId={card.id}
                  shortCode={card.short_code}
                  title={card.title}
                  cardType={card.card_type}
                  levels={card.levels}
                  xpMax={card.xp_max}
                  rarity={card.rarity}
                  colorTheme={card.color_theme}
                  creature={importedCreature ?? card.creature}
                  evolved={previewEvolved}
                  domainLabel={card.domain_label}
                  content={previewContent}
                  summaryFallback={card.rule || card.method}
                />
              </ScaledCard>
            </div>
          </div>
          <span
            className={cn(
              'inline-block rounded-full px-2 py-0.5 text-xs font-semibold',
              FIT_LABEL[fit].className,
            )}
          >
            {FIT_LABEL[fit].text}
          </span>
          <p className="text-ink-500 break-all text-xs">
            QR code : <span className="text-ink-700">{buildCardQrUrl(card.short_code)}</span>
          </p>
        </section>

        {/* Actions + édition */}
        <section className="space-y-6">
          <div className="border-ink-200 space-y-3 rounded-2xl border bg-white p-4">
            <h2 className="text-ink-900 font-display text-base font-bold">Publier la carte</h2>

            {card.status === 'generated' && (
              <div className="space-y-2">
                <label className="flex min-h-[2.75rem] cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    className="h-5 w-5"
                    checked={reviewed}
                    onChange={(e) => setReviewed(e.target.checked)}
                  />
                  <span className="text-ink-800 text-sm">
                    J’ai relu le contenu généré et je le valide.
                  </span>
                </label>
                <Button
                  fullWidth
                  disabled={!reviewed || dirty || pending}
                  onClick={onValidate}
                  className="sm:w-auto"
                >
                  <span className="flex items-center justify-center gap-2">
                    <CheckCircle2 className="h-4 w-4" /> Valider le contenu
                  </span>
                </Button>
                {dirty && (
                  <p className="text-ink-500 text-xs">
                    Enregistre d’abord tes modifications du recto.
                  </p>
                )}
              </div>
            )}

            {(card.status === 'review' || card.status === 'published') && (
              <div className="space-y-2">
                {issues.length > 0 && (
                  <ul className="text-accent-600 list-disc space-y-1 pl-5 text-sm">
                    {issues.map((i) => (
                      <li key={i}>{i}</li>
                    ))}
                  </ul>
                )}
                {warnings.map((w) => (
                  <p key={w} className="text-ink-500 text-xs">
                    {w}
                  </p>
                ))}
                <Button
                  fullWidth
                  className="sm:w-auto"
                  disabled={issues.length > 0 || dirty || pending}
                  loading={publish.isPending}
                  onClick={onPublish}
                >
                  <span className="flex items-center justify-center gap-2">
                    <Send className="h-4 w-4" />
                    {card.status === 'published' ? 'Republier cette version' : 'Publier'}
                  </span>
                </Button>
              </div>
            )}

            {card.status === 'draft' && (
              <p className="text-ink-500 text-sm">Génère d’abord le recto avec l’IA.</p>
            )}

            <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:flex-wrap">
              <Link
                to="/tutor/cards/$cardId/print"
                params={{ cardId: card.id }}
                className="w-full sm:w-auto"
              >
                <Button variant="outline" fullWidth>
                  <span className="flex items-center justify-center gap-2">
                    <Printer className="h-4 w-4" /> Imprimer (63 × 88)
                  </span>
                </Button>
              </Link>
              {card.published_version && (
                <Link to="/card/$cardId" params={{ cardId: card.id }} className="w-full sm:w-auto">
                  <Button variant="outline" fullWidth>
                    <span className="flex items-center justify-center gap-2">
                      <ExternalLink className="h-4 w-4" /> Voir en ligne
                    </span>
                  </Button>
                </Link>
              )}
              {!archived && (
                <Link
                  to="/tutor/cards/new"
                  search={{ parent: card.id }}
                  className="w-full sm:w-auto"
                >
                  <Button variant="outline" fullWidth>
                    <span className="flex items-center justify-center gap-2">
                      <GitBranchPlus className="h-4 w-4" /> Créer une carte dérivée
                    </span>
                  </Button>
                </Link>
              )}
              {archived ? (
                <Button
                  variant="ghost"
                  fullWidth
                  className="sm:w-auto"
                  onClick={onRestore}
                  disabled={pending}
                >
                  <span className="flex items-center justify-center gap-2">
                    <ArchiveRestore className="h-4 w-4" /> Restaurer
                  </span>
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  fullWidth
                  className="sm:w-auto"
                  onClick={onArchive}
                  disabled={pending}
                >
                  <span className="flex items-center justify-center gap-2">
                    <Archive className="h-4 w-4" /> Archiver
                  </span>
                </Button>
              )}
            </div>
          </div>

          {!archived && (
            <div className="border-ink-200 space-y-3 rounded-2xl border bg-white p-4">
              <h2 className="text-ink-900 font-display text-base font-bold">
                {card.structured_content.sections.length === 0
                  ? 'Générer le recto'
                  : 'Régénérer avec l’IA'}
              </h2>
              <label className="block space-y-1">
                <span className="text-ink-600 text-xs font-semibold">
                  Remarques pour l’IA (optionnel)
                </span>
                <textarea
                  className="border-ink-200 text-ink-900 w-full rounded-lg border bg-white px-3 py-2 text-sm"
                  rows={2}
                  maxLength={1000}
                  placeholder="Plus court, ajoute un tableau des cas…"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                />
              </label>
              <AiModelSelect value={model} onChange={setModel} disabled={pending} />
              <Button
                fullWidth
                className="sm:w-auto"
                loading={generate.isPending}
                disabled={pending}
                onClick={onGenerate}
              >
                <span className="flex items-center justify-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  {card.structured_content.sections.length === 0 ? 'Générer le recto' : 'Régénérer'}
                </span>
              </Button>
            </div>
          )}

          {!archived && (
            <CardCreaturePanel
              card={card}
              accent={palette.accent}
              onPreview={(c) => {
                setImportedCreature(c);
                if (c) setPreviewEvolved(false);
              }}
              onMessage={setMessage}
            />
          )}

          {!archived && (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-ink-900 font-display text-base font-bold">Modifier le recto</h2>
                <Button
                  size="sm"
                  disabled={!dirty || !draftValid || pending}
                  loading={saveContent.isPending}
                  onClick={() => void onSave()}
                >
                  Enregistrer
                </Button>
              </div>
              {card.status === 'review' && (
                <p className="text-ink-500 text-xs">
                  Modifier le recto annule la validation : il faudra le relire à nouveau.
                </p>
              )}
              <CardSectionsEditor
                content={card.structured_content}
                onChange={(content, valid) => {
                  setDraft(content);
                  setDraftValid(valid);
                  setDirty(true);
                }}
              />
            </div>
          )}

          <CardQuestionsPanel
            cardId={card.id}
            published={card.published_version !== null}
            questionXp={effectiveCardQuestionXp(card.question_xp, card.xp_max)}
          />

          <CardToolsEditor card={card} readOnly={archived} />

          {/* Animation pédagogique (moteur JRAF, lot 3) : admins seulement au départ */}
          {admin && !archived && <CardAnimationPanel cardId={card.id} />}

          <div className="border-ink-200 space-y-3 rounded-2xl border bg-white p-4">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-ink-900 font-display text-base font-bold">Informations</h2>
              {!editingInfo && !archived && (
                <button
                  type="button"
                  onClick={() => setEditingInfo(true)}
                  className="text-primary-600 flex min-h-[2.75rem] items-center gap-1.5 text-sm font-semibold"
                >
                  <Pencil className="h-4 w-4" /> Modifier
                </button>
              )}
            </div>
            {editingInfo ? (
              <CardInfoForm
                cardId={card.id}
                initial={toInput(card)}
                submitLabel="Enregistrer"
                submitting={update.isPending}
                onSubmit={(input) => void onSaveInfo(input)}
                onCancel={() => setEditingInfo(false)}
              />
            ) : (
              <dl className="text-ink-700 grid grid-cols-1 gap-2 text-sm">
                <div>
                  <dt className="text-ink-500 text-xs font-semibold">Filiation</dt>
                  <dd className="mt-1">
                    <CardFiliationEditor card={card} />
                  </dd>
                </div>
                {card.topic && (
                  <div>
                    <dt className="text-ink-500 text-xs">Sujet</dt>
                    <dd>{card.topic}</dd>
                  </div>
                )}
                {card.lessons.length > 0 && (
                  <div>
                    <dt className="text-ink-500 text-xs">Cours associés</dt>
                    <dd>{card.lessons.map((l) => l.title).join(', ')}</dd>
                  </div>
                )}
                {card.method && (
                  <div>
                    <dt className="text-ink-500 text-xs">Méthode fournie</dt>
                    <dd className="whitespace-pre-line">{card.method}</dd>
                  </div>
                )}
              </dl>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
